<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main class="content-page">
      <section class="content-shell">
      <p class="eyebrow">
        <span class="eyebrow-line"></span>
        Contact us
      </p>

      <h1>
        Tell us about the project
        <span>and the site conditions.</span>
      </h1>

      <p class="lead">
        Share the type of works, building type, location and timeline. We will review the requirements and
        advise on a practical approach — including whether rope access or conventional access suits the job.
      </p>

      <div class="contact-layout">
        <form class="contact-form" @submit.prevent="submitForm">
          <div class="form-row">
            <label>
              <span>Name <i>*</i></span>
              <input v-model="form.name" required type="text" placeholder="Your name" />
            </label>

            <label>
              <span>Company / MCST</span>
              <input v-model="form.company" type="text" placeholder="Organisation name" />
            </label>
          </div>

          <div class="form-row">
            <label>
              <span>Email <i>*</i></span>
              <input v-model="form.email" required type="email" placeholder="name@company.com" />
            </label>

            <label>
              <span>Contact number</span>
              <input v-model="form.phone" type="tel" placeholder="+65 0000 0000" />
            </label>
          </div>

          <label>
            <span>Type of works</span>
            <select v-model="form.service">
              <optgroup v-for="group in serviceGroups" :key="group.label" :label="group.label">
                <option v-for="option in group.options" :key="option" :value="option">
                  {{ option }}
                </option>
              </optgroup>
            </select>
          </label>

          <label>
            <span>Site location</span>
            <input v-model="form.location" type="text" placeholder="Building name, address or area" />
          </label>

          <label>
            <span>Project details <i>*</i></span>
            <textarea
              v-model="form.message"
              required
              rows="6"
              placeholder="Describe the issue, building height, affected areas and any timeline constraints."
            ></textarea>
          </label>

          <!-- Honeypot: hidden from people, filled in by most bots. -->
          <label class="form-botcheck" aria-hidden="true">
            <span>Leave this field empty</span>
            <input v-model="botcheck" type="checkbox" tabindex="-1" autocomplete="off" />
          </label>

          <div v-if="captchaEnabled" class="form-captcha">
            <div ref="captcha"></div>
            <p v-if="captchaState === 'blocked'" class="form-captcha-note">
              The captcha could not load — it may be blocked by your browser or network. Please
              reach us on WhatsApp instead.
            </p>
          </div>

          <button class="btn btn-primary" type="submit" :disabled="isSending">
            {{ isSending ? "Sending enquiry…" : "Send Enquiry" }}
            <ArrowRight v-if="!isSending" />
          </button>

          <p v-if="status === 'success'" class="form-status form-status-ok" role="status">
            Thank you — your enquiry has been sent. We will review the details and reply by email.
          </p>

          <p v-else-if="status === 'error'" class="form-status form-status-error" role="alert">
            {{ errorMessage }} You can also reach us on WhatsApp at
            <a :href="whatsappUrl" target="_blank" rel="noopener noreferrer">{{ whatsappNumber }}</a>.
          </p>

          <p class="form-note">
            Your enquiry is emailed to our team. Prefer WhatsApp?
            <button class="form-link" type="button" @click="sendViaWhatsApp">
              Send these details on WhatsApp instead
            </button>
            — it opens with your details pre-filled so you can review before sending.
          </p>
        </form>

        <aside class="contact-aside">
          <div class="aside-card">
            <h2>Blue Speed Construction Pte. Ltd.</h2>
            <p>{{ overview.intro }}</p>
            <p>{{ overview.scope }}</p>

            <div class="aside-badges">
              <span v-for="item in bcaRegistrations" :key="item.code">BCA {{ item.code }}</span>
            </div>
          </div>

          <div class="aside-card">
            <span class="aside-label">Direct WhatsApp</span>
            <a class="aside-link" :href="whatsappUrl" target="_blank" rel="noopener noreferrer">
              {{ whatsappNumber }}
            </a>
            <p class="aside-hint">Send site photos or drawings directly through WhatsApp.</p>
          </div>

          <div class="aside-card">
            <span class="aside-label">What happens next</span>
            <ol class="aside-steps">
              <li v-for="step in nextSteps" :key="step">{{ step }}</li>
            </ol>
          </div>
        </aside>
      </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { ArrowRight } from "lucide-vue-next";

import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import {
  HCAPTCHA_SITEKEY,
  WEB3FORMS_ACCESS_KEY,
  WHATSAPP_DIGITS,
  WHATSAPP_DISPLAY,
  whatsappLink,
} from "@/config/company";

const HCAPTCHA_SCRIPT = "https://js.hcaptcha.com/1/api.js?render=explicit";
import { bcaRegistrations, overview, services } from "@/content/company-content";

export default {
  name: "ContactPage",

  components: {
    ArrowRight,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      form: {
        name: "",
        company: "",
        email: "",
        phone: "",
        service: "General building maintenance",
        location: "",
        message: "",
      },

      // Honeypot field — a real person never sees or ticks this.
      botcheck: false,

      // "idle" | "sending" | "success" | "error"
      status: "idle",
      errorMessage: "",

      // "off" | "loading" | "ready" | "blocked"
      captchaState: HCAPTCHA_SITEKEY ? "loading" : "off",
      captchaToken: "",
      captchaWidgetId: null,

      // Mirrors the services page so the two never drift apart.
      serviceGroups: [
        { label: "Core services", options: services.core },
        { label: "Other services", options: services.other },
        { label: "Rope access", options: ["Rope access works", "Not sure — please advise"] },
      ],

      overview,
      bcaRegistrations,

      nextSteps: [
        "We review the scope, photos and site constraints.",
        "We advise on a workable access method and approach.",
        "A site inspection or quotation is arranged where needed.",
      ],

      whatsappNumber: WHATSAPP_DISPLAY,
      whatsappDigits: WHATSAPP_DIGITS,
      whatsappUrl: whatsappLink(),
    };
  },

  computed: {
    isSending() {
      return this.status === "sending";
    },

    captchaEnabled() {
      return this.captchaState !== "off";
    },
  },

  mounted() {
    if (this.captchaEnabled) this.loadCaptcha();
  },

  methods: {
    /**
     * Loads hCaptcha on demand so the third-party script only costs the contact
     * page, not every route.
     */
    async loadCaptcha() {
      try {
        if (!window.hcaptcha) {
          await new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${HCAPTCHA_SCRIPT}"]`);

            if (existing) {
              existing.addEventListener("load", resolve);
              existing.addEventListener("error", reject);
              return;
            }

            const script = document.createElement("script");
            script.src = HCAPTCHA_SCRIPT;
            script.async = true;
            script.defer = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        this.captchaWidgetId = window.hcaptcha.render(this.$refs.captcha, {
          sitekey: HCAPTCHA_SITEKEY,
          callback: (token) => {
            this.captchaToken = token;
          },
          "expired-callback": () => {
            this.captchaToken = "";
          },
          "error-callback": () => {
            this.captchaToken = "";
          },
        });

        this.captchaState = "ready";
      } catch {
        // Blocked by an extension, a network filter or an hCaptcha outage.
        this.captchaState = "blocked";
      }
    },

    resetCaptcha() {
      this.captchaToken = "";

      // hCaptcha tokens are single-use, so the widget has to be re-armed.
      if (this.captchaState === "ready" && this.captchaWidgetId !== null) {
        window.hcaptcha.reset(this.captchaWidgetId);
      }
    },

    async submitForm() {
      if (this.isSending) return;

      if (this.captchaState === "ready" && !this.captchaToken) {
        this.status = "error";
        this.errorMessage = "Please complete the captcha before sending.";
        return;
      }

      this.status = "sending";
      this.errorMessage = "";

      try {
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: `Website enquiry — ${this.form.service}`,
            from_name: "Blue Speed website",
            name: this.form.name,
            email: this.form.email,
            "Company / MCST": this.form.company || "-",
            "Contact number": this.form.phone || "-",
            "Type of works": this.form.service,
            "Site location": this.form.location || "-",
            message: this.form.message,
            botcheck: this.botcheck,
            "h-captcha-response": this.captchaToken,
          }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Your enquiry could not be sent.");
        }

        this.status = "success";
        this.resetForm();
      } catch (error) {
        this.status = "error";
        this.errorMessage =
          error instanceof TypeError
            ? "We could not reach our mail service — please check your connection and try again."
            : error.message;
      } finally {
        this.resetCaptcha();
      }
    },

    resetForm() {
      this.form.name = "";
      this.form.company = "";
      this.form.email = "";
      this.form.phone = "";
      this.form.service = "General building maintenance";
      this.form.location = "";
      this.form.message = "";
    },

    sendViaWhatsApp() {
      const lines = [
        "Hi Blue Speed, I would like to enquire about a project.",
        "",
        `Name: ${this.form.name || "-"}`,
        `Company / MCST: ${this.form.company || "-"}`,
        `Email: ${this.form.email || "-"}`,
        `Contact number: ${this.form.phone || "-"}`,
        `Type of works: ${this.form.service}`,
        `Site location: ${this.form.location || "-"}`,
        "",
        "Project details:",
        this.form.message || "-",
      ];

      window.open(
        `https://wa.me/${this.whatsappDigits}?text=${encodeURIComponent(lines.join("\n"))}`,
        "_blank",
        "noopener"
      );
    },
  },
};
</script>

<style scoped>
.content-page {
  position: relative;
  z-index: 2;
  padding: 0 0 96px;
}

.content-shell {
  width: min(1200px, calc(100% - 40px));
  margin: 152px auto 0;
}

h1 {
  max-width: 780px;
  margin: 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: clamp(34px, 4.4vw, 58px);
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: -0.045em;
}

h1 span {
  display: block;
  color: var(--muted-light);
}

.lead {
  max-width: 720px;
  margin: 24px 0 0;
}

.contact-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(300px, 0.75fr);
  gap: 20px;
  margin-top: 48px;
  align-items: start;
}

.contact-form {
  display: grid;
  gap: 18px;
  padding: 32px;
  border: 1px solid #d6e3ef;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(20, 60, 105, 0.06);
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

label {
  display: grid;
  gap: 8px;
}

label > span {
  color: #0b2545;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

label i {
  color: #ef3b2d;
  font-style: normal;
}

input,
textarea,
select {
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #d6e4f2;
  background: #ffffff;
  color: #1f3755;
  font: inherit;
  font-size: 14px;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;
}

input::placeholder,
textarea::placeholder {
  color: #9db2c7;
}

input:focus,
textarea:focus,
select:focus {
  outline: none;
  border-color: #0b5dbb;
  box-shadow: 0 0 0 3px rgba(11, 93, 187, 0.12);
}

textarea {
  resize: vertical;
}

.contact-form .btn {
  width: 100%;
  min-height: 56px;
  margin-top: 6px;
}

.contact-form .btn:disabled {
  cursor: progress;
  opacity: 0.65;
}

/* Honeypot — kept in the layout flow for bots, invisible to people. */
.form-botcheck {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.form-captcha {
  display: grid;
  gap: 10px;
  margin-top: 2px;
}

/* The hCaptcha iframe is a fixed 303px wide; let it shrink on small screens. */
.form-captcha :deep(iframe) {
  max-width: 100%;
}

.form-captcha-note {
  margin: 0;
  color: #a5322a;
  font-size: 11.5px;
  line-height: 1.6;
}

.form-status {
  margin: 0;
  padding: 13px 14px;
  border: 1px solid;
  font-size: 12.5px;
  line-height: 1.65;
}

.form-status-ok {
  border-color: #b6dfc4;
  background: #f2fbf5;
  color: #1c6b3a;
}

.form-status-error {
  border-color: #f3c4bf;
  background: #fff5f4;
  color: #a5322a;
}

.form-status a {
  color: inherit;
  font-weight: 700;
}

.form-note {
  margin: 0;
  color: #7691ac;
  font-size: 11px;
  line-height: 1.6;
}

.form-link {
  padding: 0;
  border: 0;
  background: none;
  color: #0b5dbb;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.form-link:hover {
  color: #0b2545;
}

.contact-aside {
  display: grid;
  gap: 14px;
}

.aside-card {
  padding: 26px;
  border: 1px solid #d6e3ef;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(20, 60, 105, 0.06);
}

.aside-card h2 {
  margin: 0;
  color: #0b2545;
  font-family: "Manrope", sans-serif;
  font-size: 19px;
  line-height: 1.25;
  letter-spacing: -0.03em;
}

.aside-card p {
  margin: 14px 0 0;
  color: #5b7590;
  font-size: 12.5px;
  line-height: 1.75;
}

.aside-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.aside-badges span {
  padding: 7px 10px;
  border: 1px solid #cfdeeb;
  background: #f7fbff;
  color: #0b5dbb;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
}

.aside-label {
  display: block;
  color: #6e8aa6;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.aside-link {
  display: inline-block;
  margin-top: 12px;
  color: #0b2545;
  font-family: "Manrope", sans-serif;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-decoration: none;
}

.aside-link:hover {
  color: #0b5dbb;
}

.aside-hint {
  margin: 10px 0 0;
  color: #7691ac;
  font-size: 11.5px;
  line-height: 1.6;
}

.aside-steps {
  display: grid;
  gap: 12px;
  margin: 16px 0 0;
  padding: 0 0 0 18px;
  color: #33526f;
  font-size: 12.5px;
  line-height: 1.6;
}

.aside-steps li::marker {
  color: #0b5dbb;
  font-weight: 700;
}

@media (max-width: 980px) {
  .contact-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .content-shell {
    width: min(1200px, calc(100% - 32px));
    margin-top: 116px;
  }

  .contact-form {
    padding: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
